import {useQueryClient} from '@tanstack/react-query';
import {useUpdateBusinessPartnerOnboarding} from 'api/business-partner';
import ControllingOptionsForm from 'components/pages/OnboardingPage/components/ControllingOptionsForm';
import OwnerForm from 'components/pages/OnboardingPage/components/OwnerForm';
import StepTitle from 'components/pages/OnboardingPage/components/StepTitle';
import {findOnboardingStepByNumber} from 'components/pages/OnboardingPage/onboardingSteps';
import {IDefaultStepProps} from 'components/pages/OnboardingPage/useOnboarding';
import Form, {FormOnSubmit} from 'components/ui/Form/Form/Form';
import useModal from 'components/ui/ModalContainer/useModal';
import {USER_CACHE} from 'constants/cache';
import {LEGAL_FORM_SINGLE_PERSON_ENTERPRISE} from 'constants/common';
import {handleFormError} from 'helpers/handleFormError';
import {useForm} from 'react-hook-form';
import {IUpdateBusinessPartnerOnboarding} from 'types/api';
import styles from './Steps.module.scss';
import NextStepButton from 'components/pages/OnboardingPage/components/NextStepButton';
import {useMemo} from 'react';

interface IFormValues {
    gwgInfo?: {
        formOfControl: string;
        assetsOwner: string;
    };
    'type-1'?: 'yes' | 'no';
    'type-2'?: 'yes' | 'no';
    'type-3'?: 'yes' | 'no';
    formErrors?: string;
}

const FormOfControl = (props: IDefaultStepProps) => {
    const {
        isIndividual,
        activeBusinessPartner,
        setActiveBusinessPartner,
        onSuccess,
        currentStep,
    } = props;
    const {mutate: addAdditionalInfo, isPending: isLoading} =
        useUpdateBusinessPartnerOnboarding();
    const {closeModal} = useModal();
    const queryClient = useQueryClient();

    const formMethods = useForm<IFormValues>({
        defaultValues: {
            gwgInfo: {
                assetsOwner: activeBusinessPartner.gwgInfo?.assetsOwner
                    ? 'yes'
                    : 'no',
                formOfControl:
                    activeBusinessPartner.gwgInfo?.formOfControl?.toString() ??
                    '',
            },
            'type-1': undefined,
            'type-2': undefined,
            'type-3': undefined,
        },
        shouldUnregister: false,
    });

    const isSinglePerson =
        isIndividual ||
        activeBusinessPartner.legalForm === LEGAL_FORM_SINGLE_PERSON_ENTERPRISE;

    const getSubtitle = useMemo(() => {
        if (isSinglePerson) {
            return activeBusinessPartner.amnisLegalEntityCountry
                .split('/')
                .pop() === 'CH' ? (
                'In accordance with Article 27 of the Agreement on the Swiss banks’ code of conduct with regard to the exercise of due diligence (CDB 20), the contracting partner hereby declares that the person(s) listed below is/are the beneficial owner(s) of the assets deposited under the above relationship. If the contracting partner is the beneficial owner of the assets, the contracting partner’s details must be set out below:'
            ) : (
                <span>
                    In the following section you will be asked to disclose the
                    ownership of{' '}
                    <var data-var="company-name">
                        {activeBusinessPartner.companyName}
                    </var>
                    . This information is being collected in order to comply
                    with the Due Diligence Ordinance (Art. 3 Para. 1 Lit. a.)
                </span>
            );
        }
        return activeBusinessPartner.amnisLegalEntityCountry
            .split('/')
            .pop() === 'CH' ? (
            'Pursuant to Article 20 of the Agreement on the Swiss banks’ code of conduct with regard to the exercise of due diligence (CDB 20), the contracting partner(s) hereby declare(s) (tick the appropriate box):'
        ) : (
            <span>
                In the following section you will be asked to disclose the
                ownership of{' '}
                <var data-var="company-name">
                    {activeBusinessPartner.companyName}
                </var>
                . This information is being collected in order to comply with
                the Due Diligence Ordinance (Art. 3 Para. 1 Lit. b.)
            </span>
        );
    }, [isSinglePerson, activeBusinessPartner]);

    const handleSubmit: FormOnSubmit<IFormValues> = (values, setError) => {
        let stepData: IUpdateBusinessPartnerOnboarding;
        if (!values.gwgInfo) {
            throw new Error('missing gwgInfo: ' + JSON.stringify(values));
        }
        if (isSinglePerson) {
            stepData = {
                businessPartnerId: activeBusinessPartner['@id'],
                step: findOnboardingStepByNumber(currentStep)?.type,
                gwgInfo: {
                    id: activeBusinessPartner.gwgInfo?.id,
                    formOfControl: 4,
                    assetsOwner: values.gwgInfo.assetsOwner === 'yes',
                },
            };
        } else {
            const {
                gwgInfo: {formOfControl, assetsOwner},
            } = values;

            stepData = {
                businessPartnerId: activeBusinessPartner['@id'],
                step: findOnboardingStepByNumber(currentStep)?.type,
                gwgInfo: {
                    id: activeBusinessPartner.gwgInfo?.id,
                    assetsOwner: assetsOwner === 'yes',
                    formOfControl: parseInt(formOfControl, 10),
                },
            };
        }

        addAdditionalInfo(stepData, {
            onSuccess: response => {
                setActiveBusinessPartner(response);
                closeModal();
                onSuccess();
                queryClient.invalidateQueries({queryKey: [USER_CACHE]});
            },
            onError: (requestErrors: any) => {
                handleFormError(requestErrors, setError);
            },
        });
    };
    return (
        <Form onSubmit={handleSubmit} formMethods={formMethods}>
            <div className={styles.form}>
                <StepTitle title={'Form of control'} subTitle={getSubtitle} />
                {!isSinglePerson ? (
                    <ControllingOptionsForm
                        name="gwgInfo.formOfControl"
                        defaultValue={
                            activeBusinessPartner.gwgInfo?.formOfControl ??
                            undefined
                        }
                        label=""
                        variant="dark"
                    />
                ) : null}
            </div>
            <OwnerForm activeBusinessPartner={activeBusinessPartner} />
            <div className={styles.form}>
                <NextStepButton disabled={false} />
            </div>
        </Form>
    );
};

export default FormOfControl;
